import axios from 'axios';
import pMap from 'p-map-browser';

import getLogger from '../util/logger';
import { photoUploadSuccess } from './photo-upload';
import { sentryCaptureMessage } from '../util/sentry';
import {
  photosAndVideosUpload,
  photosUpload,
} from '../apps/pro/redux/fileUpload/fileUploadActions';

const log = getLogger('AlbumFolderUploadAction');
/**
 * Captures photo errors *per-photo*.
 * The errors array will be reported to sentry.
 */
let errors = [];
/**
 * Counts the number of photos that have been processed on each upload.
 * This is used to - at the end of the batch - we reload the
 * query in which we depend upon to keep UI info consistent.
 */
let processedPhotos = 0;
/**
 * If we only refresh on the -last- photo
 * upload, and that one fails,  the data across the UI
 * will be entirely inconsistent. To avoid this,
 * we'll have a max number of refreshes we will be performing.
 */
const refreshTimes = 5;

/**
 * Number of photos to upload concurrently.
 */
const CONCURRENCY = 3;

/**
 * Max number of attempts to assign a photo.
 */
const MAX_ASSIGN_ATTEMPTS = 3;

export const ALBUM_FOLDER_UPLOAD_REQUEST = 'albumFolder/UPLOAD_REQUEST';
export const ALBUM_FOLDER_UPLOAD_SUCCESS = 'albumFolder/UPLOAD_SUCCESS';
export const ALBUM_FOLDER_UPLOAD_FAILURE = 'albumFolder/UPLOAD_FAILURE';
export const ALBUM_FOLDER_UPLOAD_SET_PHOTO_BATCH =
  'albumFolder/UPLOAD_SET_PHOTO_BATCH';
export const ALBUM_FOLDER_PHOTO_UPLOAD_SUCCESS =
  'albumFolder/PHOTO_UPLOAD_SUCCESS';
export const ALBUM_FOLDER_PHOTO_UPLOAD_FAILURE =
  'albumFolder/PHOTO_UPLOAD_FAILURE';
export const ALBUM_FOLDER_RESET = 'albumFolder/UPLOAD_RESET';
export const ALBUM_FOLDER_CANCEL_UPLOAD_REQUEST =
  'albumFolder/CANCEL_UPLOAD_REQUEST';
export const ALBUM_FOLDER_CANCEL_UPLOAD_SUCCESS =
  'albumFolder/CANCEL_UPLOAD_SUCCESS';
export const ALBUM_FOLDER_RETRY = 'albumFolder/RETRY';

const albumFolderUploadRequest = photos => {
  return { type: ALBUM_FOLDER_UPLOAD_REQUEST, photos };
};

const albumFolderUploadSetPhotoBatch = photoBatch => {
  return { type: ALBUM_FOLDER_UPLOAD_SET_PHOTO_BATCH, photoBatch };
};

const albumFolderUploadSuccess = (uploadedPhotoItemIds, isRetry) => {
  return { type: ALBUM_FOLDER_UPLOAD_SUCCESS, uploadedPhotoItemIds, isRetry };
};

const albumFolderUploadFailure = error => {
  return { type: ALBUM_FOLDER_UPLOAD_FAILURE, error };
};

const albumFolderPhotoUploadSuccess = photo => {
  return { type: ALBUM_FOLDER_PHOTO_UPLOAD_SUCCESS, photo };
};

const albumFolderPhotoUploadFailure = (photo, error) => {
  return { type: ALBUM_FOLDER_PHOTO_UPLOAD_FAILURE, photo, error };
};

const albumFolderPhotoUploadRetry = () => {
  return { type: ALBUM_FOLDER_RETRY };
};

const onPhotoUploaded =
  ({
    albumFolderId,
    photos,
    albumFolderAddPhotosMutation,
    assignPhotos,
    dispatch,
    getState,
    currentRetryAttempt = 1,
    albumAddPhotosMutation,
    isContributor,
    albumPhotoStatus,
    albumPhotosAlbumFolderTagsCreateMutation,
    processPhotoForegrounds,
    albumPhotoScheduleMap,
  }) =>
  async ({ file: photo, photoId, error }) => {
    try {
      const refreshEveryNPhotos = Math.max(
        1,
        Math.floor(photos.length / refreshTimes),
      );

      const { targetAlbums, assignedAlbumFolderTagIds } =
        getState().albumFolderPhotos;

      // Here we consider a photo to be processed.
      // It is considered processed even if the upload
      // inherently failed.
      processedPhotos += 1;

      // Means that this photo will no be counted whatsoever among the
      // valid files.
      if (axios.isCancel(error)) {
        return null;
      }

      if (error) {
        dispatch(albumFolderPhotoUploadFailure(photo, error));
        errors.push(error);
        return null;
      }

      if (error) {
        return null;
      }

      const shouldRefresh =
        processedPhotos % refreshEveryNPhotos === 0 ||
        processedPhotos >= photos.length - 1 - CONCURRENCY;

      if (shouldRefresh) {
        log.info(
          'albumFolderUploadPhotos :: Data will be refreshed at processed photos:  ',
          processedPhotos,
        );
      }

      if (isContributor || albumPhotoScheduleMap) {
        await pMap(targetAlbums, async targetAlbum => {
          await albumAddPhotosMutation({
            albumId: targetAlbum.albumId,
            photoGroupId: targetAlbum.photoGroupId,
            photoIds: [photoId],
            albumPhotoStatus,
            processPhotoForegrounds,
            photoScheduleId: albumPhotoScheduleMap?.[targetAlbum.albumId],
          });
        });

        dispatch(
          photoUploadSuccess(
            {
              id: photoId,
              file: photo,
            },
            true,
          ),
        );

        dispatch(albumFolderPhotoUploadSuccess({ id: photoId, file: photo }));

        log.info('albumFolderUploadPhotos :: Photo added to albumFolder');
        return photoId;
      }

      const targetAlbumIds = targetAlbums
        .filter(targetAlbum => !targetAlbum.photoGroupId)
        .map(({ albumId }) => albumId);

      const targetAlbumPhotoGroupIds = targetAlbums
        .filter(targetAlbum => targetAlbum.photoGroupId)
        .map(({ photoGroupId }) => photoGroupId);

      const addPhotosRes = await albumFolderAddPhotosMutation({
        albumFolderId,
        photoIds: [photoId], // Add a single photo to the albumFolder.
        refetch: shouldRefresh, // Refetch
        processPhotoForegrounds,
        ...(assignPhotos
          ? {
              // Targets
              targetAlbumIds,
              targetAlbumPhotoGroupIds,
              albumPhotoStatus,
            }
          : {}),
      });

      if (
        albumPhotosAlbumFolderTagsCreateMutation &&
        assignedAlbumFolderTagIds.length > 0
      ) {
        await pMap(targetAlbumIds, async albumId => {
          await albumPhotosAlbumFolderTagsCreateMutation({
            albumId,
            photoIds: [photoId],
            albumFolderTagIds: assignedAlbumFolderTagIds,
          });
        });
      }

      const {
        data: {
          albumFolderAddPhotos: {
            album_folder_photos: albumFolderUploadedPhotoItems,
          },
        },
      } = addPhotosRes;

      // Only a single item is expected here
      // as there was a single photo upload
      // so just get the first.
      const [albumFolderUploadedPhotoItem] = albumFolderUploadedPhotoItems;

      dispatch(
        photoUploadSuccess(
          {
            id: photoId,
            file: photo,
          },
          true,
        ),
      );

      dispatch(albumFolderPhotoUploadSuccess({ id: photoId, file: photo }));

      log.info('albumFolderUploadPhotos :: Photo added to albumFolder');
      return albumFolderUploadedPhotoItem.id;
    } catch (albumFolderPhotoAssignError) {
      if (currentRetryAttempt > MAX_ASSIGN_ATTEMPTS) {
        // A photo might fail here.
        dispatch(
          albumFolderPhotoUploadFailure(photo, albumFolderPhotoAssignError),
        );
        errors.push(albumFolderPhotoAssignError);
        return null;
      }

      // Will retry to assign the photo.
      return onPhotoUploaded({
        albumFolderId,
        photos,
        albumFolderAddPhotosMutation,
        assignPhotos,
        dispatch,
        getState,
        currentRetryAttempt: currentRetryAttempt + 1,
        albumPhotoStatus,
      })({ file: photo, photoId, error });
    }
  };

const onVideoUploaded =
  ({
    videoUpdateMutation,
    dispatch,
    getState,
    currentRetryAttempt = 1,
    isContributor,
  }) =>
  async ({ file: video, videoId, error }) => {
    try {
      if (isContributor) {
        log.info('onVideoUploaded :: Contributor video uploaded');
        dispatch(
          photoUploadSuccess(
            {
              id: videoId,
              file: video,
            },
            true,
          ),
        );
        return videoId;
      }

      const { targetAlbums } = getState().albumFolderPhotos;

      // Means that this photo will no be counted whatsoever among the
      // valid files.
      if (axios.isCancel(error)) {
        return null;
      }

      if (error) {
        dispatch(albumFolderPhotoUploadFailure(video, error));
        errors.push(error);
        return null;
      }

      if (error) {
        return null;
      }

      dispatch(albumFolderPhotoUploadSuccess({ id: videoId, file: video }));

      await videoUpdateMutation({
        video_id: videoId,
        video_links: {
          album_ids: targetAlbums
            .filter(targetAlbum => !targetAlbum.photoGroupId)
            .map(({ albumId }) => albumId),
        },
      });

      dispatch(
        photoUploadSuccess(
          {
            id: videoId,
            file: video,
          },
          true,
        ),
      );

      log.info('onVideoUploaded :: Video added to targets');

      return videoId;
    } catch (albumFolderVideoAssignError) {
      if (currentRetryAttempt > MAX_ASSIGN_ATTEMPTS) {
        // A photo might fail here.
        dispatch(
          albumFolderPhotoUploadFailure(video, albumFolderVideoAssignError),
        );
        errors.push(albumFolderVideoAssignError);
        return null;
      }

      // Will retry to assign the video
      return onVideoUploaded({
        videoUpdateMutation,
        dispatch,
        getState,
        currentRetryAttempt: currentRetryAttempt + 1,
        isContributor,
      })({ file: video, videoId, error });
    }
  };

/**
 * Upload photos to the albumFolder, adding them to it in the process. Handles the Photo Batch creation process,
 * but does not upload the photos itself.
 *
 * This method also ensures that the queries are refreshed every so often so that integrity accross the UI can be
 * consistent.
 *
 * @param {Object} opts List of Options.
 * @param {Object} opts.photos List of photos to upload.
 * @param {function} opts.photoBatchPhotosUploadMutation Mutation to create a photo batch photo.
 * @param {function} opts.photoBatchPhotosUpdateMutation Mutation to update a photo batch photo during the upload.
 * @param {function} opts.albumFolderAddPhotosMutation Mutation to add a photo to the albumFolder after its been uploaded.
 * @param {function} opts.dispatch Redux dispatch.
 * @param {function} opts.getState Redux getState method.
 * @param {function} opts.photoBatchCreateMutation Mutation to create a Photo batch record.
 * @param {boolean} opts.assignPhotos Whether the photos will be assigned directly into the target albums or not.
 * @param {function} opts.videosUploadMutation Mutation to upload list of video files
 * @param {function} opts.videoUpdateMutation Mutation to update video files
 * @param {function} opts.videoDeleteMutation Mutation to delete video files
 * @param {Function} opts.multipartUploadCompleteMutation Mutation to abort a multipart record.
 * @param {Function} opts.multipartUploadAbortMutation Mutation to abort a multipart record.
 * @param {string} opts.albumFolderId
 * @param {function} opts.albumAddPhotosMutation Mutation to add photos to albums. Used only on contributors.
 * @param {boolean} opts.isContributor Whether or not current user is a contributor.
 * @param {string} opts.albumPhotoStatus ALBUM_PHOTO_STATUS the photo will be assigned as. Only relevant when assignment is happening..
 * @param {function} opts.albumPhotosAlbumFolderTagsCreateMutation Mutation to add tags to photo albums.
 * @param {Boolean} opts.processPhotoForegrounds Wether or not to process photo foregrounds
 * @param {Object} opts.albumPhotoScheduleMap Map of albumId to photoScheduleId for scheduled uploads
 */
const albumFolderUploadPhotos = async ({
  albumFolderId,
  photos,
  photoBatchPhotosUploadMutation,
  photoBatchPhotosUpdateMutation,
  albumFolderAddPhotosMutation,
  dispatch,
  getState,
  photoBatchCreateMutation,
  assignPhotos,
  videosUploadMutation,
  videoUpdateMutation,
  videoDeleteMutation,
  multipartUploadCompleteMutation,
  multipartUploadAbortMutation,
  albumAddPhotosMutation,
  isContributor,
  albumPhotoStatus,
  albumPhotosAlbumFolderTagsCreateMutation,
  processPhotoForegrounds,
  albumPhotoScheduleMap,
}) => {
  // eslint-disable-next-line no-useless-catch
  try {
    const args = {
      photoBatchCreateMutation,
      photoBatchPhotosUploadMutation,
      photoBatchPhotosUpdateMutation,
      onPhotoUploadProcessComplete: onPhotoUploaded({
        albumFolderId,
        photos,
        albumFolderAddPhotosMutation,
        getState,
        dispatch,
        assignPhotos,
        albumAddPhotosMutation,
        isContributor,
        albumPhotoStatus,
        albumPhotosAlbumFolderTagsCreateMutation,
        processPhotoForegrounds,
        albumPhotoScheduleMap,
      }),
      onPhotoBatchCreated: photoBatch =>
        dispatch(albumFolderUploadSetPhotoBatch(photoBatch)),
      dispatch,
      getState,
    };

    const albumFolderUploadedPhotosIds = assignPhotos
      ? await photosAndVideosUpload({
          ...args,
          files: photos,
          videosUploadMutation,
          videoUpdateMutation,
          videoDeleteMutation,
          multipartUploadCompleteMutation,
          multipartUploadAbortMutation,
          onVideoUploadProcessComplete: onVideoUploaded({
            videoUpdateMutation,
            dispatch,
            getState,
            isContributor,
          }),
          albumPhotoStatus,
        })
      : await photosUpload({
          photos,
          ...args,
        });

    log.info(
      'albumFolderUploadPhotos :: Album folder photos: ',
      albumFolderUploadedPhotosIds,
    );

    return albumFolderUploadedPhotosIds
      .map(({ processCompleteResult: id }) => id)
      .filter(id => !!id);
  } catch (error) {
    throw error;
  }
};

export const albumFolderUpload =
  ({
    accountId,
    albumFolderId,
    photos,
    photoBatchPhotosUploadMutation,
    photoBatchPhotosUpdateMutation,
    albumFolderAddPhotosMutation,
    isRetry,
    photoBatchCreateMutation,
  }) =>
  async (dispatch, getState) => {
    errors = [];
    processedPhotos = 0;
    const {
      albumFolderUploads: { failedPhotos },
    } = getState();

    const photosToUpload = isRetry
      ? failedPhotos.map(({ photo }) => photo)
      : photos;

    if (isRetry) {
      dispatch(albumFolderPhotoUploadRetry());
      log.info(
        'albumFolderUpload() :: The following photos will be retried: ',
        failedPhotos,
      );
    }

    log.info('albumFolderUpload() :: Uploading photos...', photosToUpload);
    dispatch(albumFolderUploadRequest(photosToUpload));

    try {
      const albumFolderUploadedPhotosIds = await albumFolderUploadPhotos({
        albumFolderId,
        photos: photosToUpload,
        photoBatchPhotosUploadMutation,
        photoBatchPhotosUpdateMutation,
        albumFolderAddPhotosMutation,
        dispatch,
        getState,
        photoBatchCreateMutation,
      });

      if (errors.length > 0) {
        sentryCaptureMessage(
          '[UCP - albumFolderUpload] - Errors found while uploading photos.',
          {
            level: 'error',
            extra: {
              errors,
              albumFolderId,
              accountId,
            },
          },
        );
      }

      log.info(
        'albumFolderUpload() :: Uploaded photos...',
        albumFolderUploadedPhotosIds,
      );

      dispatch(albumFolderUploadSuccess(albumFolderUploadedPhotosIds, isRetry));
      log.info('albumFolderUpload() :: Upload finished');
    } catch (error) {
      log.error('albumFolderUpload() :: Error:', error);

      sentryCaptureMessage('[UCP - error after success]', {
        level: 'error',
        extra: {
          error,
          albumFolderId,
          accountId,
        },
      });

      dispatch(albumFolderUploadFailure(error));
    }
  };

export const concurrentAlbumUpload =
  ({
    accountId,
    albumFolderId,
    photos,
    photoBatchPhotosUploadMutation,
    photoBatchPhotosUpdateMutation,
    albumFolderAddPhotosMutation,
    isRetry,
    photoBatchCreateMutation,
    videosUploadMutation,
    videoUpdateMutation,
    videoDeleteMutation,
    multipartUploadCompleteMutation,
    multipartUploadAbortMutation,
    albumAddPhotosMutation,
    isContributor,
    albumPhotoStatus,
    albumPhotosAlbumFolderTagsCreateMutation,
    processPhotoForegrounds,
    photoScheduleCreateMutation,
    scheduledAt,
  }) =>
  async (dispatch, getState) => {
    errors = [];
    processedPhotos = 0;
    const {
      albumFolderUploads: { failedPhotos },
    } = getState();

    const photosToUpload = isRetry
      ? failedPhotos.map(({ photo }) => photo)
      : photos;

    if (isRetry) {
      dispatch(albumFolderPhotoUploadRetry());
      log.info(
        'retryConcurrentAlbumUpload() :: The following photos will be retried: ',
        failedPhotos,
      );
    }

    log.info('concurrentAlbumUpload() :: Uploading photos...', photosToUpload);
    dispatch(albumFolderUploadRequest(photosToUpload));

    try {
      let albumPhotoScheduleMap = null;
      if (scheduledAt && photoScheduleCreateMutation) {
        const { targetAlbums } = getState().albumFolderPhotos;
        const albumIds = [
          ...new Set(
            targetAlbums.filter(t => !t.photoGroupId).map(t => t.albumId),
          ),
        ];
        const scheduleEntries = await pMap(albumIds, async albumId => {
          const result = await photoScheduleCreateMutation({
            albumId,
            scheduledAt,
          });
          const scheduleId =
            result?.data?.photoScheduleCreate?.photo_schedule?.id;
          return [albumId, scheduleId];
        });
        albumPhotoScheduleMap = Object.fromEntries(scheduleEntries);
        log.info(
          'concurrentAlbumUpload() :: Created photo schedules:',
          albumPhotoScheduleMap,
        );
      }

      const albumFolderUploadedPhotosIds = await albumFolderUploadPhotos({
        albumFolderId,
        photos: photosToUpload,
        photoBatchPhotosUploadMutation,
        photoBatchPhotosUpdateMutation,
        albumFolderAddPhotosMutation,
        dispatch,
        getState,
        photoBatchCreateMutation,
        assignPhotos: true,
        videosUploadMutation,
        videoUpdateMutation,
        videoDeleteMutation,
        multipartUploadCompleteMutation,
        multipartUploadAbortMutation,
        albumAddPhotosMutation,
        isContributor,
        albumPhotoStatus,
        albumPhotosAlbumFolderTagsCreateMutation,
        processPhotoForegrounds,
        albumPhotoScheduleMap,
      });

      // In old times, this method used to care for assigning the photos
      // by using `albumFolderAssign` mutation. As of PR #1751 this is now handled by the `albumFolderUploadPhotos`
      // method above. Cancelling the upload has also changed from removing all old photos to just avoiding uploading more.

      if (errors.length > 0) {
        sentryCaptureMessage(
          '[UCP - concurrentAlbumUpload] - Errors found while uploading photos.',
          {
            level: 'error',
            extra: {
              errors,
              albumFolderId,
              accountId,
            },
          },
        );
      }

      dispatch(albumFolderUploadSuccess(albumFolderUploadedPhotosIds, isRetry));
      log.info(
        'concurrentAlbumUpload() :: Upload finished to all targetAlbums',
      );
    } catch (error) {
      log.error('concurrentAlbumUpload() :: Error:', error);

      sentryCaptureMessage('[UCP - error after success]', {
        level: 'error',
        extra: {
          error,
          albumFolderId,
          accountId,
        },
      });

      dispatch(albumFolderUploadFailure(error));
    }
  };
